/* ──────────────────────────────────────────────────────────────────────
 *  DimensionPanelHeader — header bar for a single My Impact dimension cell.
 *
 *  Layout:  [colored dimension icon] [Dimension name]  ……  [goal medal]
 *  with the header's bottom edge doubling as the progress bar (no extra
 *  vertical space).
 *
 *  Medal semantics (locked design, Skip 2026-06-15):
 *    - The EARNED medals live only in the radar legend. This header shows
 *      the GOAL — the next tier up from the user's current tier — as an
 *      up-arrow + abbreviation (BRZ / AG / AU / PT) in the target metal's
 *      color, dashed outline so it never reads as "earned."
 *    - The underline fills toward that goal (progress %).
 *    - At Platinum (no next tier) the goal collapses to a solid PT badge.
 *
 *  i18n NOTE: dimension display names are hardcoded here, mirroring the
 *  DimensionRadar precedent. Medal abbreviations are symbols (not localized).
 *  Route the names through useAppTranslation when the radar's labels move.
 * ────────────────────────────────────────────────────────────────────── */

import React from 'react';
import { ArrowUp } from 'lucide-react';
import { DIMENSION_CONFIG } from '../DimensionRadar/dimensionConfig';
import type { DimKey } from '../DimensionRadar/dimensionConfig';
import styles from './DimensionPanelHeader.module.css';

export type MedalTier = 'none' | 'bronze' | 'silver' | 'gold' | 'platinum';

type Earnable = Exclude<MedalTier, 'none'>;

const TIER_ORDER: MedalTier[] = ['none', 'bronze', 'silver', 'gold', 'platinum'];

const MEDAL_ABBR: Record<Earnable, string> = {
  bronze: 'BRZ',
  silver: 'AG',
  gold: 'AU',
  platinum: 'PT',
};

const MEDAL_COLOR: Record<Earnable, string> = {
  bronze: 'var(--medal-bronze, #CD7F32)',
  silver: 'var(--medal-silver, #B0B8C4)',
  gold: 'var(--medal-gold, #D4A843)',
  platinum: 'var(--medal-platinum, #C8C6C2)',
};

const DIM_NAMES: Record<DimKey, string> = {
  mind: 'Mind',
  voice: 'Voice',
  body: 'Body',
  heart: 'Heart',
  wings: 'Wings',
  roots: 'Roots',
};

/** Next tier up from the current one; null when already at platinum. */
function nextTier(current: MedalTier): Earnable | null {
  const i = TIER_ORDER.indexOf(current);
  const n = TIER_ORDER[i + 1];
  return (n as Earnable | undefined) ?? null;
}

interface DimensionPanelHeaderProps {
  dim: DimKey;
  /** The user's CURRENT tier from the Impact Signature. The goal shown is
      the next tier up. Defaults to 'none' (goal = Bronze). */
  medal?: MedalTier;
  /** Progress toward the next tier, 0–100. Placeholder until DEV-1305. */
  progressPct?: number;
}

export default function DimensionPanelHeader({
  dim,
  medal = 'none',
  progressPct = 0,
}: DimensionPanelHeaderProps) {
  const cfg = DIMENSION_CONFIG[dim];
  const Icon = cfg.icon;
  const goal = nextTier(medal);
  const pct = Math.max(0, Math.min(100, Math.round(progressPct)));

  return (
    <div
      className={styles.header}
      style={{ '--dim-color': cfg.color } as React.CSSProperties}
    >
      <span className={styles.identity}>
        <Icon className={styles.icon} color={cfg.color} size={20} strokeWidth={2.2} aria-hidden="true" />
        <span className={styles.name}>{DIM_NAMES[dim]}</span>
      </span>

      {goal ? (
        <span
          className={styles.goal}
          style={{
            color: MEDAL_COLOR[goal],
            borderColor: `color-mix(in srgb, ${MEDAL_COLOR[goal]} 60%, transparent)`,
          }}
          aria-label={`${pct}% toward ${MEDAL_ABBR[goal]}`}
        >
          <ArrowUp className={styles.goalIcon} size={13} strokeWidth={2.4} aria-hidden="true" />
          {MEDAL_ABBR[goal]}
        </span>
      ) : (
        <span
          className={styles.goal}
          style={{
            color: MEDAL_COLOR.platinum,
            borderColor: `color-mix(in srgb, ${MEDAL_COLOR.platinum} 60%, transparent)`,
            borderStyle: 'solid',
          }}
          aria-label="Platinum reached"
        >
          {MEDAL_ABBR.platinum}
        </span>
      )}

      <div className={styles.track} />
      <div className={styles.fill} style={{ width: `${pct}%` }} />
    </div>
  );
}
